import React, { useState, useEffect } from 'react';
import { ExternalLink, Download, Mail, Phone, Award, Handshake, Target, Globe } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { partnersAPI } from '../services/api';

const Partners = () => {
  const { t, language } = useLanguage();
  const [mainPartners, setMainPartners] = useState<any[]>([]);
  const [supportPartners, setSupportPartners] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPartners();
  }, [language]);

  const fetchPartners = async () => {
    try {
      setLoading(true);
      
      // Fetch main partners (title, official, institutional)
      const mainResponse = await partnersAPI.getAll({ 
        category: 'title,official,institutional' 
      });
      
      // Fetch support partners (support, equipment)
      const supportResponse = await partnersAPI.getAll({ 
        category: 'support,equipment' 
      });
      
      if (mainResponse.records) {
        setMainPartners(mainResponse.records.map((partner: any) => ({
          ...partner,
          description: language === 'fr' ? partner.description_fr : partner.description_en,
          testimonial: language === 'fr' ? partner.testimonial_fr : partner.testimonial_en
        })));
      }
      
      if (supportResponse.records) {
        setSupportPartners(supportResponse.records);
      }
    } catch (error) {
      console.error('Error fetching partners:', error);
      // Fallback data
      setMainPartners([
        {
          name: 'Red Bull',
          category: t('partners.category.title'),
          logo_url: 'https://images.pexels.com/photos/1650299/pexels-photo-1650299.jpeg?auto=compress&cs=tinysrgb&w=200',
          description: language === 'fr' 
            ? 'Partenaire principal pour le développement du surf extrême et des événements majeurs.'
            : 'Main partner for extreme surfing development and major events.',
          partnership_since: 2023,
          website: 'redbull.com',
          testimonial: language === 'fr'
            ? 'L\'ASCsurf représente l\'avenir du surf mondial. Nous sommes fiers de soutenir cette vision.'
            : 'ASCsurf represents the future of world surfing. We are proud to support this vision.'
        }
      ]);
      
      setSupportPartners([
        { name: 'Rip Curl', category: t('partners.category.equipment'), logo: 'RC' },
        { name: 'Billabong', category: 'Vêtements', logo: 'BB' },
        { name: 'FCS Fins', category: 'Accessoires', logo: 'FCS' },
        { name: 'Futures Fins', category: 'Accessoires', logo: 'FF' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const partnershipBenefits = [
    {
      icon: Globe,
      title: t('partners.benefits.visibility'),
      description: t('partners.benefits.visibility.desc')
    },
    {
      icon: Target,
      title: t('partners.benefits.audience'),
      description: t('partners.benefits.audience.desc')
    },
    {
      icon: Award,
      title: t('partners.benefits.premium'),
      description: t('partners.benefits.premium.desc')
    }
  ];

  const sponsorshipLevels = [
    {
      level: t('partners.levels.title.main'),
      price: '50 000€+',
      benefits: [
        'Naming rights sur l\'Africa Surf Tour',
        'Logo sur tous les supports de communication',
        'Activation exclusive sur tous les événements',
        'Accès VIP et hospitalité premium',
        'Campagne marketing co-brandée',
        'Droits TV et streaming exclusifs'
      ],
      color: 'from-gold to-yellow-600'
    },
    {
      level: t('partners.levels.title.official'),
      price: '25 000€+',
      benefits: [
        'Logo sur les équipements des athlètes',
        'Présence sur 4 étapes du tour',
        'Activation sur les événements',
        'Contenu digital dédié',
        'Accès aux athlètes pour campagnes',
        'Rapport d\'impact détaillé'
      ],
      color: 'from-blue-600 to-teal-600'
    },
    {
      level: t('partners.levels.title.support'),
      price: '10 000€+',
      benefits: [
        'Logo sur le site web et réseaux sociaux',
        'Mention dans les communiqués de presse',
        'Invitation aux événements majeurs',
        'Newsletter dédiée aux partenaires',
        'Opportunités de networking',
        'Certificat de partenariat'
      ],
      color: 'from-orange-500 to-red-500'
    }
  ];

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-teal-800 to-blue-700 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img 
            src="https://i.imgur.com/mrt24S8.jpeg?auto=compress&cs=tinysrgb&w=1600" 
            alt="Partenaires ASCsurf"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            {t('partners.hero.title')}
          </h1>
          <p className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto mb-8">
            {t('partners.hero.subtitle')}
          </p>
          <div className="flex flex-wrap justify-center gap-8">
            <div className="bg-white/10 backdrop-blur-md px-6 py-4 rounded-lg">
              <div className="text-2xl font-bold text-orange-400">{mainPartners.length + supportPartners.length}+</div>
              <div className="text-sm text-gray-300">{t('partners.stats.partners')}</div>
            </div>

          </div>
        </div>
      </section>



      {/* Support Partners Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
             <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              {t('partners.main.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('partners.main.subtitle')}
            </p>
          </div>

<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-10 px-6 py-10 bg-gray-50">
  {supportPartners.map((partner, index) => (
    <div
      key={index}
      className="bg-white rounded-2xl shadow-lg flex flex-col items-center p-8 hover:shadow-2xl transition-shadow duration-300 cursor-pointer"
    >
      <div className="w-full h-40 mb-6 flex items-center justify-center bg-gray-100 rounded-lg overflow-hidden">
        {partner.logo_url ? (
          <img
            src={partner.logo_url}
            alt={partner.name}
            className="max-w-full max-h-full object-contain"
          />
        ) : (
          <div className="flex items-center justify-center w-full h-full bg-gray-300 rounded-lg">
            <span className="text-gray-600 text-lg font-semibold">Logo</span>
          </div>
        )}
      </div>
      <h3 className="text-lg font-semibold text-gray-900 text-center">{partner.name}</h3>
    </div>
  ))}
</div>


        </div>
      </section>

      {/* Partnership Benefits */}
<section className="py-20 bg-white">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="text-center mb-16">
      <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
        {t('partners.benefits.title')}
      </h2>
      <p className="text-xl text-gray-600 max-w-3xl mx-auto">
        {t('partners.benefits.subtitle')}
      </p>
    </div>

   <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-8 justify-center">
  {partnershipBenefits.map((benefit, index) => (
    <div key={index} className="group flex flex-col items-center text-center">
      <div className="bg-gradient-to-br from-blue-500 to-teal-500 w-16 h-16 rounded-full flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
        <benefit.icon className="h-8 w-8 text-white" />
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-4">{benefit.title}</h3>
      <p className="text-gray-600 leading-relaxed max-w-xs">{benefit.description}</p>
    </div>
  ))}
</div>

  </div>
</section>

 


      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-teal-600">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            {t('partners.join.title')}
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            {t('partners.join.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href='/contact' className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-4 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2">
              <Mail className="h-5 w-5" />
              <span>Contact</span>
            </a>
            <button className="border-2 border-white text-white hover:bg-white hover:text-blue-600 px-8 py-4 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 flex items-center justify-center space-x-2">
              <Phone className="h-5 w-5" />
              <span>+221 77 197 67 67</span>
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Partners;